import { useEffect } from "react";
import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Autocomplete from "@/components/Autocomplete";
import { useQuery } from "@apollo/client";
import { GET_PRODUCTS, GET_PRODUCT_VARIANTS } from "../queries";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { useFormContext } from "react-hook-form";
import { LineItemFormValue } from "../../edit-order/[orderId]/index";
import { sortBy, orderBy } from "lodash";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatter } from "@/lib/currencyHelpers";
import TextField from "@/components/TextField";
import { getClassification } from "../../../_utils";
import { useFlags } from "@/hooks/useFeatureFlags";

type Variant = {
  id: string;
  name: string;
  priceCents: number;
  proratedPriceCents: number;
  changeablePrice?: boolean;
  displayText?: string;
  value: string;
};

type Product = {
  id: string;
  name: string;
};

type ProductData = {
  commerceProducts: {
    nodes: Product[];
  };
};

type VariantData = {
  commerceProductVariants: {
    nodes: Variant[];
  };
};

export const AddProductModal = ({
  onDismiss,
  lineItemsWatch,
  templateId,
  orderId
}: {
  onDismiss: () => void;
  lineItemsWatch: LineItemFormValue[];
  templateId: string;
  orderId: string;
}) => {
  const flags = useFlags();
  const { setValue } = useFormContext();
  const {
    control,
    setValue: setProductValue,
    watch,
    resetField,
    trigger,
    formState: { errors }
  } = useForm();
  const [variables, setVariables] = useState({ input: { templateId } });
  const [selectedProduct, setSelectedProduct] = useState<Product>({ id: "", name: "" });
  const [selectedVariant, setSelectedVariant] = useState<Variant>({
    id: "",
    name: "",
    priceCents: 0,
    changeablePrice: false,
    value: "",
    proratedPriceCents: 0
  });
  const { data: productData } = useQuery<ProductData>(GET_PRODUCTS, { variables });
  const { close, Modal } = useModal();

  const [variantsVariables, setVariantsVariables] = useState({ productId: selectedProduct.id, templateId, orderId });
  const { data: variantData } = useQuery<VariantData>(GET_PRODUCT_VARIANTS, {
    variables: variantsVariables
  });

  const productSearchValue = watch("product");
  const variantSearchValue = watch("variant");
  const isChangeablePrice = selectedVariant.changeablePrice;

  function selectVariant(name: string, value: Variant, setValue: (name: string, value: string) => void) {
    setSelectedVariant({
      id: value.value,
      name: value.name,
      priceCents: value.priceCents,
      changeablePrice: value.changeablePrice,
      value: value.value,
      proratedPriceCents: value.proratedPriceCents
    });
    setValue(name, `${value.displayText}`);
  }

  function selectProduct(name: string, value: Product, setValue: (name: string, value: string) => void) {
    setSelectedProduct({ id: value.id, name: value.name });
    setValue(name, `${value.name}`);
    resetField("variant");
    setVariantsVariables({ productId: value.id, templateId, orderId });
  }

  async function onContinue() {
    const result = await trigger();
    if (result) {
      setValue(
        "lineItems",
        [
          ...lineItemsWatch,
          ...[
            {
              id: `${Date.now()}`,
              originalId: selectedVariant.id,
              name: selectedVariant.name,
              quantity: 1,
              priceCents: isChangeablePrice
                ? Number(watch("price"))
                : Number(
                    (flags["backoffice-prorated-product-price"] && selectedVariant?.proratedPriceCents) ||
                      selectedVariant?.priceCents
                  ),
              displayOrder: 99,
              action: "add"
            }
          ]
        ],
        { shouldDirty: true }
      );
      close();
    }
  }

  useEffect(() => {
    setVariables(() => ({
      input: {
        templateId,
        ...(productSearchValue ? { search: productSearchValue } : {})
      }
    }));
  }, [productSearchValue, templateId]);

  useEffect(() => {
    setVariantsVariables((prevValue) => ({
      ...prevValue,
      ...(variantSearchValue ? { search: variantSearchValue } : {})
    }));
  }, [variantSearchValue]);

  const variantOptions =
    variantData?.commerceProductVariants?.nodes.map((variant) => ({
      ...getClassification(variant.name),
      id: variant.id,
      value: variant.id,
      name: variant.name,
      displayText: `${
        !variant.changeablePrice
          ? `${centsToDollarsFormatted(Number(variant.priceCents))} (${variant.name})`
          : `${variant.name}`
      }`,
      priceCents: variant.priceCents,
      changeablePrice: variant.changeablePrice,
      proratedPriceCents: variant.proratedPriceCents
    })) || [];

  return (
    <Modal
      onClosed={onDismiss}
      action={<Button onClick={onContinue}>Continue Editing</Button>}
      headline="Add Product"
      className="sm:min-w-[520px] md:min-w-[720px]"
    >
      <p className="mb-4">Select a product to add to the order.</p>
      <input className="absolute opacity-0" />
      <div className="w-full space-y-6">
        <Controller
          name="product"
          control={control}
          rules={{ required: "Product is required" }}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              label="Product"
              required
              query={value}
              data={sortBy(productData?.commerceProducts.nodes, "name") || []}
              onChangeText={onChange}
              reset={() => {
                resetField("product");
                resetField("variant");
                setSelectedProduct({ id: "", name: "" });
              }}
              onSelect={(value) => selectProduct(name, value, setProductValue)}
              renderItem={(data) => <>{data.name}</>}
              error={!!errors?.product}
              errorText={errors?.product?.message?.toString()}
              variant="searchableDropdown"
            />
          )}
        />
        <Controller
          name="variant"
          control={control}
          rules={{ required: "Price/Variant is required" }}
          render={({ field: { onChange, value, name } }) => (
            <Autocomplete
              label="Price/Variant"
              disabled={!selectedProduct.id}
              required
              query={value}
              data={
                sortBy(orderBy(variantOptions, "classificationNumber"), (item) => [
                  item.name,
                  item.classificationLetter
                ]) || []
              }
              onChangeText={onChange}
              reset={() => {
                resetField("variant");
                setSelectedVariant({ id: "", name: "", priceCents: 0, value: "", proratedPriceCents: 0 });
                setVariantsVariables({ productId: selectedProduct.id, templateId, orderId });
              }}
              onSelect={(value) => selectVariant(name, value, setProductValue)}
              renderItem={(data) => <>{data.displayText}</>}
              error={!!errors?.variant}
              errorText={errors?.variant?.message?.toString()}
              variant="searchableDropdown"
            />
          )}
        />
        {isChangeablePrice && (
          <Controller
            render={({ field }) => {
              return (
                <NumberFormatBase
                  disabled={false}
                  value={(field.value / 100).toFixed(2) || 0}
                  format={currencyFormatter}
                  customInput={TextField}
                  error={!!errors?.price}
                  errorText={errors?.price?.message?.toString()}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onValueChange={(values) => {
                    field.onChange(Math.round(values.floatValue || 0));
                  }}
                  leadingIcon="attach_money"
                  label="Price *"
                  className="w-full"
                />
              );
            }}
            control={control}
            name="price"
            rules={{
              validate: {
                required: (value) => {
                  if (!value && isChangeablePrice) return "Price is required";
                  return true;
                }
              }
            }}
          />
        )}
      </div>
    </Modal>
  );
};
